import React, { useState } from 'react';
import APP_CONFIG from '../config/appConfig';
import { useSettings } from '../lib/SettingsContext';
import './SettingsPage.css';

const SettingsPage = () => {
    // These values are team-wide and stored in the database, so this page edits
    // the shared store rather than a local copy that nothing reads.
    const {
        values: calculationSettings,
        setValues: setCalculationSettings,
        meta,
        save,
        reset
    } = useSettings();

    const [editedBy, setEditedBy] = useState('');
    const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | local
    const [saveNote, setSaveNote] = useState(null);

    const resetToDefaults = () => {
        reset();
        setSaveState('idle');
    };

    const handleSave = async () => {
        setSaveState('saving');
        const result = await save(calculationSettings, { updatedBy: editedBy.trim() || null });
        setSaveState(result.persisted ? 'saved' : 'local');
        setSaveNote(result.persisted
            ? `Saved as version ${result.version}. Everyone on the team now prices with these values.`
            : `Could not reach the settings service${result.reason ? ` (${result.reason})` : ''}. Kept in this browser only — save again once it is back.`);
    };

    const sourceLabel = {
        loading: 'Loading…',
        database: meta.version ? `Shared, version ${meta.version}` : 'Shared',
        local: 'This browser only — not saved to the team',
        defaults: 'Built-in defaults'
    }[meta.source] || meta.source;

    return (
        <div className="settings-page">
            <div className="page-header">
                <h1>Calculation Settings</h1>
                <p className="page-description">
                    Fine-tune the calculation parameters used in pricing and projections. These settings are based on
                    real-world data from 200+ content owners across various streaming platforms.
                </p>
            </div>

            <div className="settings-container">
                <div className="settings-content">
                    {/* Left side - Information and Documentation */}
                    <div className="settings-info-panel">
                        <div className="info-section">
                            <h3>🧮 Calculation Formulas</h3>

                            <div className="formula-group">
                                <h4>📊 Bandwidth</h4>
                                <div className="formula-code">
                                    <code>
                                        baseBandwidthGB = totalPlays × (avgWatchTime/60) × streamingBitrate × (30×24) × 0.125{'\n'}
                                        monthlyBandwidthGB = baseBandwidthGB × (1/bandwidthEfficiency) × peakUsageFactor
                                    </code>
                                </div>
                            </div>

                            <div className="formula-group">
                                <h4>💾 Storage</h4>
                                <div className="formula-code">
                                    <code>
                                        contentStorageGB = videoAssets × (avgDuration/60) × storagePerHour{'\n'}
                                        totalStorageGB = contentStorageGB × storageOverhead
                                    </code>
                                </div>
                            </div>

                            <div className="formula-group">
                                <h4>📈 Projections</h4>
                                <div className="formula-code">
                                    <code>
                                        growthMultiplier = 1 + (monthlyGrowthRate / 100){'\n'}
                                        quarterlyRevenue = baseRevenue × Math.pow(growthMultiplier, quarterIndex × 3) × 3
                                    </code>
                                </div>
                            </div>
                        </div>

                        <div className="info-section">
                            <h3>📋 Data Source & Accuracy</h3>
                            <p>These parameters are based on real-world data from <strong>over 200 content owners</strong> across various streaming platforms and industries.</p>

                            <div className="data-sources">
                                <span className="source-tag">✓ 200+ Content Owners</span>
                                <span className="source-tag">✓ Multiple CDN Providers</span>
                                <span className="source-tag">✓ Various Industries</span>
                                <span className="source-tag">✓ Real Production Data</span>
                            </div>

                            <p><strong>Recommendation:</strong> If you have real-world experience with streaming costs, feel free to adjust these parameters to match your specific use case, CDN provider, or user behavior patterns for more accurate pricing estimates.</p>
                        </div>

                        <div className="info-section">
                            <h3>🔢 Key Constants</h3>
                            <ul className="constants-list">
                                <li><strong>0.125</strong> - Converts Mbps to GB/hour</li>
                                <li><strong>30×24</strong> - Hours in a month</li>
                                <li><strong>/60</strong> - Minutes to hours conversion</li>
                                <li><strong>/1000</strong> - GB to TB conversion</li>
                            </ul>
                        </div>
                    </div>

                    {/* Right side - Settings Controls */}
                    <div className="settings-controls-panel">
                        <div className="control-section">
                            <h3>🎥 Streaming Bitrates</h3>

                            {Object.entries(APP_CONFIG.calculationSettings.streamingBitrates).map(([quality, config]) => (
                                <div key={quality} className="setting-item compact">
                                    <div className="setting-header">
                                        <span className="setting-label">{quality}</span>
                                        <span className="setting-value">
                                            {calculationSettings.streamingBitrates?.[quality] || config.value} {config.unit}
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min={config.min}
                                        max={config.max}
                                        step={config.step}
                                        value={calculationSettings.streamingBitrates?.[quality] || config.value}
                                        onChange={(e) => {
                                            const newValue = parseFloat(e.target.value);
                                            setCalculationSettings(prev => ({
                                                ...prev,
                                                streamingBitrates: {
                                                    ...prev.streamingBitrates,
                                                    [quality]: newValue
                                                }
                                            }));
                                        }}
                                    />
                                    <div className="setting-description">{config.description}</div>
                                </div>
                            ))}
                        </div>

                        <div className="control-section">
                            <h3>💾 Storage Requirements</h3>

                            {Object.entries(APP_CONFIG.calculationSettings.storageRequirements).map(([quality, config]) => (
                                <div key={quality} className="setting-item compact">
                                    <div className="setting-header">
                                        <span className="setting-label">{quality}</span>
                                        <span className="setting-value">
                                            {calculationSettings.storageRequirements?.[quality] || config.value} {config.unit}
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min={config.min}
                                        max={config.max}
                                        step={config.step}
                                        value={calculationSettings.storageRequirements?.[quality] || config.value}
                                        onChange={(e) => {
                                            const newValue = parseFloat(e.target.value);
                                            setCalculationSettings(prev => ({
                                                ...prev,
                                                storageRequirements: {
                                                    ...prev.storageRequirements,
                                                    [quality]: newValue
                                                }
                                            }));
                                        }}
                                    />
                                    <div className="setting-description">{config.description}</div>
                                </div>
                            ))}
                        </div>

                        <div className="control-section">
                            <h3>⚡ Efficiency Factors</h3>

                            {Object.entries(APP_CONFIG.calculationSettings.efficiencyFactors).map(([factor, config]) => (
                                <div key={factor} className="setting-item compact">
                                    <div className="setting-header">
                                        <span className="setting-label">
                                            {factor.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                        </span>
                                        <span className="setting-value">
                                            {(calculationSettings.efficiencyFactors?.[factor] || config.value) *
                                                (config.unit.includes('%') ? 100 : 1)}{config.unit.replace('%', '%')}
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min={config.min}
                                        max={config.max}
                                        step={config.step}
                                        value={calculationSettings.efficiencyFactors?.[factor] || config.value}
                                        onChange={(e) => {
                                            const newValue = parseFloat(e.target.value);
                                            setCalculationSettings(prev => ({
                                                ...prev,
                                                efficiencyFactors: {
                                                    ...prev.efficiencyFactors,
                                                    [factor]: newValue
                                                }
                                            }));
                                        }}
                                    />
                                    <div className="setting-description">{config.description}</div>
                                </div>
                            ))}
                        </div>

                        <div className="control-section">
                            <h3>📈 Projection Settings</h3>

                            {Object.entries(APP_CONFIG.calculationSettings.projectionSettings).map(([setting, config]) => (
                                <div key={setting} className="setting-item compact">
                                    <div className="setting-header">
                                        <span className="setting-label">
                                            {setting.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                        </span>
                                        <span className="setting-value">
                                            {calculationSettings.projectionSettings?.[setting] || config.value} {config.unit}
                                        </span>
                                    </div>
                                    <input
                                        type="range"
                                        min={config.min}
                                        max={config.max}
                                        step={config.step}
                                        value={calculationSettings.projectionSettings?.[setting] || config.value}
                                        onChange={(e) => {
                                            const newValue = parseFloat(e.target.value);
                                            setCalculationSettings(prev => ({
                                                ...prev,
                                                projectionSettings: {
                                                    ...prev.projectionSettings,
                                                    [setting]: newValue
                                                }
                                            }));
                                        }}
                                    />
                                    <div className="setting-description">{config.description}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="settings-save-bar">
                    <div className="save-status">
                        <span className={`save-source source-${meta.source}`}>{sourceLabel}</span>
                        {meta.updatedBy && <span className="save-by">last changed by {meta.updatedBy}</span>}
                        {meta.updatedAt && (
                            <span className="save-when">{new Date(meta.updatedAt).toLocaleString()}</span>
                        )}
                    </div>
                    <div className="save-actions">
                        <input
                            type="text"
                            className="save-who"
                            placeholder="Your name (optional)"
                            value={editedBy}
                            onChange={(e) => setEditedBy(e.target.value)}
                        />
                        <button
                            type="button"
                            className="save-settings-btn"
                            onClick={handleSave}
                            disabled={saveState === 'saving'}
                        >
                            {saveState === 'saving' ? 'Saving…' : 'Save for the team'}
                        </button>
                    </div>
                    {saveNote && (
                        <p className={`save-note ${saveState === 'local' ? 'warn' : 'ok'}`}>{saveNote}</p>
                    )}
                </div>

                <div className="settings-footer">
                    <button
                        className="reset-settings-button"
                        onClick={resetToDefaults}
                    >
                        Reset to Defaults
                    </button>
                    <div className="settings-note">
                        <p>
                            <strong>Note:</strong> These values calibrate every quote the team issues.
                            They are stored centrally, so saving applies them for everyone. Quotes
                            already shared with a customer keep the settings they were priced on and
                            will not change.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SettingsPage;
